import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { storeResources } from '../resources';
import { INotification, INotificationsInitialValues } from './types';
import { getStorageNotifications, setStorageNotifications, LOCAL_STORAGE_TOKEN } from '../../constants/storage';
import { decodeAccessToken } from '../../utils/getDecodedAccessToken';
import {
  deleteNotificationsByChatIdFromIndexedDB,
  deleteNotificationByIdFromIndexedDB,
  deleteAllNotificationsForUser,
} from '../../utils/indexedDB';
import { logOutUser } from '../auth';

const getUserIdFromToken = (): string | null => {
  try {
    const token = localStorage.getItem(LOCAL_STORAGE_TOKEN);
    if (!token) return null;
    const decoded = decodeAccessToken(token);
    return decoded.contactId;
  } catch {
    return null;
  }
};

const initialState: INotificationsInitialValues = {
  notifications: [],
};

const notificationsSlice = createSlice({
  name: storeResources.NOTIFICATIONS,
  initialState,
  extraReducers: builder => {
    builder.addCase(logOutUser, state => {
      const userId = getUserIdFromToken();
      state[storeResources.NOTIFICATIONS] = [];
      // Delete all notifications from IndexedDB
      if (userId) {
        deleteAllNotificationsForUser(userId).catch(error => {
          console.error('Failed to delete all notifications from IndexedDB on logout:', error);
        });
      }
    });
  },
  reducers: {
    addNotification: (state, action: PayloadAction<INotification>) => {
      state[storeResources.NOTIFICATIONS].push(action.payload);
      const userId = getUserIdFromToken();
      // Save asynchronously - don't wait for completion
      setStorageNotifications(state[storeResources.NOTIFICATIONS], userId).catch(error => {
        console.error('Failed to save notification:', error);
      });
    },

    removeNotification: (state, action: PayloadAction<number>) => {
      state[storeResources.NOTIFICATIONS] = state[storeResources.NOTIFICATIONS].filter(
        notification => notification.chatId !== action.payload,
      );
      const userId = getUserIdFromToken();
      // Delete from IndexedDB
      if (userId) {
        deleteNotificationsByChatIdFromIndexedDB(action.payload, userId).catch(error => {
          console.error('Failed to delete notifications by chatId from IndexedDB:', error);
          // Fallback to saving full list
          setStorageNotifications(state[storeResources.NOTIFICATIONS], userId).catch(err => {
            console.error('Failed to save notifications after removal:', err);
          });
        });
      }
    },

    removeAllNotificationsByChatId: (state, action: PayloadAction<number>) => {
      const notificationsToRemove = state[storeResources.NOTIFICATIONS].filter(
        notification => notification.chatId === action.payload,
      );
      state[storeResources.NOTIFICATIONS] = state[storeResources.NOTIFICATIONS].filter(
        notification => notification.chatId !== action.payload,
      );
      const userId = getUserIdFromToken();
      // Delete from IndexedDB
      if (userId) {
        // Delete each notification individually from IndexedDB
        Promise.all(
          notificationsToRemove.map(notification =>
            deleteNotificationByIdFromIndexedDB(notification.id, userId),
          ),
        ).catch(error => {
          console.error('Failed to delete notifications from IndexedDB:', error);
          // Fallback to saving full list
          setStorageNotifications(state[storeResources.NOTIFICATIONS], userId).catch(err => {
            console.error('Failed to save notifications after removal:', err);
          });
        });
      }
    },

    setNotifications: (state, action: PayloadAction<INotification[]>) => {
      state[storeResources.NOTIFICATIONS] = action.payload;
    },

    clearAllNotifications: (state) => {
      const userId = getUserIdFromToken();
      state[storeResources.NOTIFICATIONS] = [];
      // Delete all notifications from IndexedDB
      if (userId) {
        deleteAllNotificationsForUser(userId).catch(error => {
          console.error('Failed to delete all notifications from IndexedDB:', error);
        });
      }
    },
  },
});

export default notificationsSlice.reducer;
export const {
  addNotification,
  removeNotification,
  removeAllNotificationsByChatId,
  setNotifications,
  clearAllNotifications,
} = notificationsSlice.actions;
